import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useManageTeamRequest,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
  usePracticeShortQuery,
  useTeamNamesQuery,
  useTeamQuery,
} from '@common/cache';
import { createManageTeamSchema } from '../schemas/manageTeam.schema';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { paths } from '@common/types';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { AdminPanelDrawerContext } from '@views/pages/Admin/context';

interface IUseManageTeamProps {
  id: string;
}

interface ICreateTeamFormShape {
  name: string;
  description?: string | null;
  record_type: string;
  record_id: string;
  records?: { record_type: string; record_id: string }[];
  city?: string | null;
  state?: string | null;
}

const initialValues: ICreateTeamFormShape = {
  name: '',
  description: '',
  record_type: 'organization',
  record_id: '',
  records: [],
  city: '',
  state: '',
};

export const useManageTeam = ({ id }: IUseManageTeamProps) => {
  const {
    triggerClose,
    triggerTabChange,
    setIsFormClean,
    handleCloseConfirmationModal,
  } = useContext(AdminPanelDrawerContext);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    reset,
    formState: { isSubmitting, isDirty, errors, isValid },
  } = useForm<ICreateTeamFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<ICreateTeamFormShape, any, any>(
      createManageTeamSchema,
    ),
    defaultValues: initialValues,
  });

  const recordTypes = [
    { value: 'organization', label: 'Organization' },
    { value: 'partner', label: 'Partner' },
    { value: 'practice', label: 'Practice' },
  ];

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'records',
  });

  const { data: teamData } = useTeamQuery({
    teamId: id,
  });
  const { data: teamsShort = [] } = useTeamNamesQuery();
  const { data: organizationsShort = [] } = useOrganizationsShortQuery();
  const { data: partnersShort = [] } = usePartnerShortQuery();
  const { data: practicesShort = [] } = usePracticeShortQuery({});

  const { mutateAsync, isPending } = useManageTeamRequest({ id });

  useEffect(() => {
    if (teamData) {
      const record_ids = teamData.additional_entities.map(record => {
        return {
          record_type: record.type as 'organization' | 'partner',
          record_id: record.id,
        };
      });
      reset({
        name: teamData.name,
        description: teamData.description,
        record_type: teamData.managing_entity.type as
          'organization' | 'partner',
        record_id: teamData.managing_entity.id,
        records: record_ids,
        city: teamData.city,
        state: teamData.state,
      });
    }
  }, [teamData, reset]);

  useEffect(() => {
    if (triggerClose && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (triggerTabChange && isDirty) {
      setIsSaveChangesModalOpen(true);
      return;
    }

    if (isDirty) {
      setIsFormClean(false);
      return;
    }

    if (triggerClose && !isDirty) {
      setIsFormClean(true);
    }

    if (triggerTabChange && !isDirty) {
      setIsFormClean(true);
    }
  }, [triggerClose, isDirty, triggerTabChange]);

  const submitHandler = async (values: ICreateTeamFormShape) => {
    try {
      await mutateAsync({
        name: values.name as paths['/team/{team_id}']['patch']['requestBody']['content']['application/json']['name'],
        description: values.description,
        record_id: values.record_id,
        record_ids: values.records?.map(r => r.record_id),
        city: values.city,
        state: values.state,
        country: '',
        team_id: id,
      });
      toast.success('Saved');
      reset(values);
      setIsFormClean(true);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
    handleCloseConfirmationModal();
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsFormClean(true);
  };

  const handleSaveChanges = async () => {
    const values = getValues();

    try {
      await mutateAsync({
        name: values.name as paths['/team/{team_id}']['patch']['requestBody']['content']['application/json']['name'],
        description: values.description,
        record_id: values.record_id,
        record_ids: values.records?.map(r => r.record_id),
        city: values.city,
        state: values.state,
        country: '',
        team_id: id,
      });
      toast.success('Saved');
      reset(values);
      setIsFormClean(true);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    control,
    handleSubmit,
    onSubmit,
    watch,
    isSubmitting,
    teamData,
    teamsShort: teamsShort.map(o => ({
      value: o,
      label: o,
    })),
    partnerShort: partnersShort.map(o => ({
      value: o.id,
      label: o.name,
    })),
    organizationsShort: organizationsShort.map(o => ({
      value: o.id,
      label: o.name,
    })),
    practicesShort: practicesShort.map(o => ({
      value: o.id,
      label: o.name,
    })),
    recordTypes,
    fields,
    append,
    remove,
    isSubmitEnabled:
      isDirty && Object.keys(errors).length === 0 && !isSubmitting,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
